import { useState } from 'react';
import { X } from 'lucide-react';
import { ToastProvider } from '../../TeacherDashboardV3/components/ui';
import AIAssistant from '../../TeacherDashboardV3/components/AIAssistant';
import { useAuth } from '../../Accounts/AuthContext';
import buffySitting from '../../assets/brand/buffy-sitting@2x.png';

// The student tutor is BUFFY (Sandy 2026-08-12) — the buffalo, not Sandy.
// Avatar = the sitting-Buffy art cropped to his face (placeholder until a
// real headshot asset exists). Exported so other student surfaces can reuse it.
export function BuffyAvatar({ size = 44 }) {
  return (
    <span
      className="relative inline-flex flex-none rounded-full overflow-hidden bg-neutral-100"
      style={{ width: size, height: size }}
    >
      <img
        src={buffySitting}
        alt="Buffy"
        className="absolute max-w-none"
        style={{ width: '112%', left: '-6%', top: '2%' }}
      />
    </span>
  );
}

// Student-side Buffy: a poppable tutor. Closed = just his face in a small
// speech bubble (tail and all), no name, no pill — minimal real estate
// (Sandy 2026-08-12). Open = the full chat panel.
function StudentBuffy() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Ask Buffy"
          aria-label="Ask Buffy"
          className="fixed bottom-6 right-5 z-40 p-2 rounded-[18px] bg-white border border-neutral-200 shadow-[0_8px_28px_rgba(20,20,20,0.14)] hover:shadow-[0_14px_40px_rgba(20,20,20,0.22)] cursor-pointer origin-bottom-right transition-[transform,box-shadow] duration-150 ease-out hover:scale-[1.08]"
        >
          <BuffyAvatar size={44} />
          {/* the speech-bubble tail: a rotated square wearing the same border
              on its two outward edges */}
          <span
            aria-hidden="true"
            className="absolute -bottom-[7px] right-[18px] w-3.5 h-3.5 bg-white border-r border-b border-neutral-200 rotate-45"
          />
        </button>
      )}
      {/* --sandy-frame-r matches this 20px clip so the assistant's gradient
          ring traces the corners (its ring is the edge; no border here). */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[380px] h-[560px] max-h-[calc(100vh-88px)] rounded-[20px] bg-white shadow-[0_16px_48px_rgba(20,20,20,0.18)] overflow-hidden text-left"
          style={{ '--sandy-frame-r': '20px' }}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Close Buffy"
            className="absolute top-4 right-4 z-10 w-7 h-7 rounded-full flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink border-0 cursor-pointer transition-colors"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
          <AIAssistant
            className="h-full"
            surface="student"
            title="Buffy"
            avatar={<BuffyAvatar size={44} />}
            subtitle="Your study buddy"
            intro="Hey, I'm Buffy. Ask me anything about what you're working on, a question you missed, or where you're stuck."
            suggestions={['Explain the question I just missed', 'What should I work on next?']}
          />
        </div>
      )}
    </>
  );
}

// Student shell: no side rail — the site navbar on top is the only chrome.
// Admin view mode leaves Buffy out: his chat would run on the admin's own
// login, not the student's. hideBuffy: an open in-class assessment (the
// homework player, 2026-10-26) is taken without a tutor.
export default function StudentShell({ children, hideBuffy = false }) {
  const { viewAs } = useAuth() || {};
  return (
    <ToastProvider>
      {/* pt-1 (was pt-3, Sandy 2026-08-12): the rail layout has no fixed top
          navbar, and the old padding read as dead space above the score. */}
      <div className="flex min-h-screen bg-white text-ink font-sans text-left antialiased pt-1">
        <main className="flex-1 min-w-0 flex flex-col">{children}</main>
      </div>
      {!viewAs && !hideBuffy && <StudentBuffy />}
    </ToastProvider>
  );
}
